// src/components/Navbar.tsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { useAudio } from "../context/AudioProvider";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Menu, MenuItem, IconButton, Avatar } from "@mui/material";
import { getUserProfile } from "../services/userService";
import type { UserProfile } from "@/types";
import { debounce } from "lodash";

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasActiveSubscription } = useAuth();
  const { state: audioState, setUIPriority } = useAudio();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isNavigating, setIsNavigating] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Track location for search state management
  const prevPathRef = useRef(location.pathname);
  const lastNavigationTimeRef = useRef<number | null>(null);
  const searchStateRef = useRef({
    searchInProgress: false,
    lastSearchQuery: "",
  });

  // Reset search when navigating away from search page
  useEffect(() => {
    if (prevPathRef.current === '/search' && location.pathname !== '/search') {
      setSearchQuery('');
    }

    // Reset navigation state when location path changes
    setIsNavigating(false);
    searchStateRef.current.searchInProgress = false;
    
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  // Update search input when the URL query params change
  useEffect(() => {
    if (location.pathname === '/search') {
      const urlQuery = new URLSearchParams(location.search).get("q");
      if (urlQuery !== null && urlQuery !== searchQuery) {
        setSearchQuery(urlQuery);
      }
    }
  }, [location.search, location.pathname]);

  // Fetch profile image for logged-in user
  useEffect(() => {
    if (user) {
      getUserProfile()
        .then((profileData) => {
          if (profileData) {
            setProfile(profileData);
            if (profileData.avatar_url) {
              setAvatarUrl(profileData.avatar_url);
            }
          }
        })
        .catch((err) => console.error("Error fetching profile in Navbar:", err));
    }
  }, [user]);

  // Safe navigation that won't interrupt audio
  const doSearch = useCallback((query: string) => {
    if (!query.trim() || isNavigating) return;

    console.log(`[Navbar] Attempting search for "${query}" - ${new Date().toISOString()}`);
    console.log(`[Navbar] Audio playing: ${audioState.isPlaying}`);

    // Prevent searching the same term twice in rapid succession
    if (query === searchStateRef.current.lastSearchQuery && 
        Date.now() - (lastNavigationTimeRef.current || 0) < 500) {
      console.log(`[Navbar] Skipping duplicate search for "${query}" (too soon)`);
      return;
    }

    // Mark as navigating
    setIsNavigating(true);
    searchStateRef.current.searchInProgress = true;
    searchStateRef.current.lastSearchQuery = query;
    lastNavigationTimeRef.current = Date.now();

    // Generate search path
    const searchPath = `/search?q=${encodeURIComponent(query.trim())}`;
    console.log(`[Navbar] Navigating to: ${searchPath}`);
    
    // NEVER use hard navigation during audio playback
    // Instead, always use React Router navigation with requestAnimationFrame
    if (audioState.isPlaying) {
      console.log(`[Navbar] Using RAF for navigation during playback`);
      // Use requestAnimationFrame to delay navigation until browser is less busy
      requestAnimationFrame(() => {
        console.log(`[Navbar] Executing navigation in RAF`);
        navigate(searchPath);
        
        // Reset navigation state after a reasonable time
        setTimeout(() => {
          console.log(`[Navbar] Navigation timeout completed, resetting state`);
          setIsNavigating(false);
          searchStateRef.current.searchInProgress = false;
        }, 500);
      });
    } else {
      // Normal navigation when no audio is playing
      console.log(`[Navbar] Direct navigation (no audio playing)`);
      navigate(searchPath);
      
      // Reset navigation state after a reasonable time
      setTimeout(() => {
        console.log(`[Navbar] Navigation timeout completed, resetting state`);
        setIsNavigating(false);
        searchStateRef.current.searchInProgress = false;
      }, 500);
    }
  }, [navigate, location, isNavigating, audioState.isPlaying]);

  // Handle real-time search as user types
  const debouncedSearch = useMemo(() => {
    // Use a shorter debounce when not playing audio for responsive typing experience
    const delay = audioState.isPlaying ? 800 : 300;
    return debounce(doSearch, delay, { leading: false, trailing: true });
  }, [doSearch, audioState.isPlaying]);

  // Handle search input changes with immediate UI update
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // If we're on the search page, update the URL immediately
    if (location.pathname === '/search') {
      const searchPath = `/search?q=${encodeURIComponent(value.trim())}`;
      navigate(searchPath, { replace: true });
    }
    
    // Don't auto-search during audio playback - the user needs to press Enter
    if (!audioState.isPlaying && value.trim().length > 1) {
      debouncedSearch(value);
    }
  };

  // Handle search form submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim() || isNavigating) return;
    
    // Cancel any pending debounced searches
    debouncedSearch.cancel();
    
    // Tell audio system to prioritize UI
    if (audioState.isPlaying) {
      console.log("[Navbar] Setting UI priority for manual search");
      setUIPriority(true);
    }
    
    // Just use regular navigation - it's more reliable
    console.log(`[Navbar] Direct navigation to search: ${searchQuery}`);
    setIsNavigating(true);
    
    // Simple approach: Just navigate and reset state after
    const searchPath = `/search?q=${encodeURIComponent(searchQuery.trim())}`;
    navigate(searchPath);
    
    // Reset state after a delay
    setTimeout(() => {
      console.log("[Navbar] Resetting navigation state");
      setIsNavigating(false);
      
      // If we're still playing audio, restore audio priority
      if (audioState.isPlaying) {
        console.log("[Navbar] Restoring audio priority");
        setUIPriority(false);
      }
    }, 800);
  };

  // Handle navigation to home or profile
  const handleNavigation = (path: string) => {
    if (isNavigating) return;
    
    // Tell audio system to prioritize UI
    if (audioState.isPlaying) {
      console.log("[Navbar] Setting UI priority for navigation");
      setUIPriority(true);
    }
    
    setIsNavigating(true);
    navigate(path);
    
    // Reset state after a delay
    setTimeout(() => {
      console.log("[Navbar] Resetting navigation state");
      setIsNavigating(false);
      
      // If we're still playing audio, restore audio priority
      if (audioState.isPlaying) {
        console.log("[Navbar] Restoring audio priority");
        setUIPriority(false);
      }
    }, 800);
  };

  // When search focus changes, inform audio system about UI priorities
  const handleSearchFocus = () => {
    searchInputRef.current?.focus();
    if (audioState.isPlaying) {
      setUIPriority(true);
    }
  };
  
  // When search loses focus, revert audio system priorities
  const handleSearchBlur = () => {
    if (audioState.isPlaying) {
      setUIPriority(false);
    }
  };

  const handleLoginClick = () => {
    navigate("/login");
  };

  const handleLogoutClick = async () => {
    await logout();
    window.location.href = "/login";
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "45px",
        backgroundColor: "#181220",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 20px",
        boxSizing: "border-box",
        zIndex: 1100,
      }}
    >
      {/* Left: Logo */}
      <div style={{ display: "flex", alignItems: "center", flexBasis: "120px" }}>
        <Link to={user ? (hasActiveSubscription ? "/discover" : "/") : "/"}>
          <img
            src="/logo2.png"
            alt="Lsnto Logo"
            style={{ maxHeight: "40px", width: "auto", cursor: "pointer" }}
          />
        </Link>
      </div>

      {/* Center: Search Bar */}
      <div
        style={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          maxWidth: "350px",
          width: "100%",
        }}
      >
        <form onSubmit={handleSearchSubmit} style={{ width: "100%", position: "relative" }}>
          <SearchIcon
            style={{
              position: "absolute",
              left: "4px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#B3B3B3",
              cursor: "pointer",
            }}
            onClick={handleSearchFocus}
          />
          <input
            type="text"
            ref={searchInputRef}
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            placeholder={isNavigating ? "Searching..." : "What do you want to Lsnto?"}
            disabled={isNavigating}
            style={{
              width: "100%",
              padding: "4px 30px",
              fontFamily: "monospace",
              borderRadius: "4px",
              border: "none",
              outline: "none",
              backgroundColor: isNavigating ? "#f5f5f5" : "#fff",
              opacity: isNavigating ? 0.8 : 1,
              cursor: isNavigating ? "wait" : "text",
            }}
          />
        </form>
      </div>

      {/* Right: Avatar or Login */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexBasis: "120px",
          justifyContent: "flex-end",
        }}
      >
        {user ? (
          <>
            <IconButton onClick={handleMenuClick} style={{ color: "#fbfafc" }}>
              {avatarUrl ? (
                <Avatar src={avatarUrl} alt="User Avatar" sx={{ width: 40, height: 40 }} />
              ) : (
                <AccountCircleIcon fontSize="large" />
              )}
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  backgroundColor: "#181220",
                  color: "#fbfafc",
                  borderRadius: "6px",
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleNavigation("/dashboard");
                }}
                style={{ fontSize: "0.875rem" }}
              >
                My Profile
              </MenuItem>
              <MenuItem onClick={handleLogoutClick} style={{ fontSize: "0.875rem" }}>
                Logout
              </MenuItem>
            </Menu>
          </>
        ) : (
          <button
            style={{
              backgroundColor: "transparent",
              color: "#E4FF1A",
              padding: "6px 16px",
              borderRadius: "20px",
              border: "2px solid #E4FF1A",
              cursor: "pointer",
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 600,
              fontSize: "0.9rem",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#E4FF1A";
              e.currentTarget.style.color = "#FF0054";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#E4FF1A";
            }}
            onClick={handleLoginClick}
          >
            Login
          </button>
        )}
      </div>
    </div>
  );
};

export default Navbar;



